import React, { useEffect, useState } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsLeftRight, faArrowsUpDown, faArrowUp, faBed, faEye, faGauge, faHeartCircleBolt, faHeartPulse, faLungs, faMoon, faPersonWalking, faSun, faTemperatureHalf, faTriangleExclamation, faWaveSquare, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import vitalsMap from './vitals_name_mapping.json';

const STD_THRESHOLD = 2;

interface VitalType {
  unit: string;
  icon: IconDefinition;
  color: string;
  group: string;
}

const VITAL_TYPES: Record<string, VitalType> = {
  'ACCELEROMETERS_STD_ACTIVE': {
    unit: 'g',
    icon: faWaveSquare,
    color: '#8b5cf6',
    group: 'movement'
  },
  'ACCELEROMETERS_STD_RESTING': {
    unit: 'g',
    icon: faWaveSquare,
    color: '#8b5cf6',
    group: 'movement'
  },
  // 'ACTIGRAPHY_COUNTS_vector_ACTIVE': {
  //   unit: 'counts',
  //   icon: faWaveSquare,
  //   color: '#7c3aed',
  //   group: 'movement'
  // },
  // 'ACTIGRAPHY_COUNTS_vector_RESTING': {
  //   unit: 'counts',
  //   icon: faWaveSquare,
  //   color: '#7c3aed',
  //   group: 'movement'
  // },
  // 'ACTIGRAPHY_COUNTS_x_ACTIVE': {
  //   unit: 'counts',
  //   icon: faArrowsLeftRight,
  //   color: '#7c3aed',
  //   group: 'movement'
  // },
  // 'ACTIGRAPHY_COUNTS_y_ACTIVE': {
  //   unit: 'counts',
  //   icon: faArrowsUpDown,
  //   color: '#7c3aed',
  //   group: 'movement'
  // },
  // 'ACTIGRAPHY_COUNTS_z_ACTIVE': {
  //   unit: 'counts',
  //   icon: faArrowUp,
  //   color: '#7c3aed',
  //   group: 'movement'
  // },
  'ACTIVITY_COUNTS_ACTIVE': {
    unit: 'counts',
    icon: faPersonWalking,
    color: '#6366f1',
    group: 'movement'
  },
  'EDA_ACTIVE': {
    unit: 'μS',
    icon: faEye,
    color: '#14b8a6',
    group: 'stress'
  },
  'MET_ACTIVE': {
    unit: 'METs',
    icon: faGauge,
    color: '#0ea5e9',
    group: 'activity'
  },
  'PRV_RESTING': {
    unit: 'ms',
    icon: faHeartCircleBolt,
    color: '#f43f5e',
    group: 'cardiac'
  },
  'PULSE_RATE_ACTIVE': {
    unit: 'bpm',
    icon: faHeartPulse,
    color: '#ef4444',
    group: 'cardiac'
  },
  'STEP_COUNTS': {
    unit: 'steps',
    icon: faPersonWalking,
    color: '#6366f1',
    group: 'activity'
  },
  'TEMPERATURE_ACTIVE': {
    unit: '°F',
    icon: faTemperatureHalf,
    color: '#f59e0b',
    group: 'temperature'
  },
  'TIME_IN_BED': {
    unit: 'time',
    icon: faBed,
    color: '#7c3aed',
    group: 'sleep'
  },
  'TOTAL_SLEEP_TIME': {
    unit: 'time',
    icon: faMoon,
    color: '#7c3aed',
    group: 'sleep'
  },
};

interface ZScoreData {
    date: string;
    
    // Active daytime measurements zscores
    ACCELEROMETERS_STD_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACCELEROMETERS_STD_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACCELEROMETERS_STD_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_x_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_x_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_x_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_y_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_y_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_y_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_z_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_z_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_z_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIVITY_COUNTS_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIVITY_COUNTS_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIVITY_COUNTS_ACTIVE_DAYTIME_MIN_zscore?: number;
    EDA_ACTIVE_DAYTIME_AVG_zscore?: number;
    EDA_ACTIVE_DAYTIME_MAX_zscore?: number;
    EDA_ACTIVE_DAYTIME_MIN_zscore?: number;
    MET_ACTIVE_DAYTIME_AVG_zscore?: number;
    MET_ACTIVE_DAYTIME_MAX_zscore?: number;
    MET_ACTIVE_DAYTIME_MIN_zscore?: number;
    PULSE_RATE_ACTIVE_DAYTIME_AVG_zscore?: number;
    PULSE_RATE_ACTIVE_DAYTIME_MAX_zscore?: number;
    PULSE_RATE_ACTIVE_DAYTIME_MIN_zscore?: number;
    STEP_COUNTS_zscore?: number;
    TEMPERATURE_ACTIVE_DAYTIME_AVG_zscore?: number;
    TEMPERATURE_ACTIVE_DAYTIME_MAX_zscore?: number;
    TEMPERATURE_ACTIVE_DAYTIME_MIN_zscore?: number;
  
    // Resting daytime and nighttime measurements zscores
    ACCELEROMETERS_STD_RESTING_DAYTIME_AVG_zscore?: number;
    ACCELEROMETERS_STD_RESTING_DAYTIME_MAX_zscore?: number;
    ACCELEROMETERS_STD_RESTING_DAYTIME_MIN_zscore?: number;
    ACCELEROMETERS_STD_RESTING_NIGHTTIME_AVG_zscore?: number;
    ACCELEROMETERS_STD_RESTING_NIGHTTIME_MAX_zscore?: number;
    ACCELEROMETERS_STD_RESTING_NIGHTTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_NIGHTTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_NIGHTTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_NIGHTTIME_MIN_zscore?: number;
    
    // Additional resting measurements zscores
    TIME_IN_BED_zscore?: number;
    TOTAL_SLEEP_TIME_zscore?: number;
    PRV_RESTING_DAYTIME_AVG_zscore?: number;
    PRV_RESTING_DAYTIME_MAX_zscore?: number;
    PRV_RESTING_DAYTIME_MIN_zscore?: number;
    PRV_RESTING_NIGHTTIME_AVG_zscore?: number;
    PRV_RESTING_NIGHTTIME_MAX_zscore?: number;
    PRV_RESTING_NIGHTTIME_MIN_zscore?: number;
    
    // Data quality metrics
    data_quality_ratio?: number;
    measurements_available?: number;
    measurements_missing?: number;
    composite_abnormality?: number;
  }
  
  interface ZScoresResponse {
    z_scores: {
      status: string;
      message: string;
      scores: ZScoreData[];
      data_quality: {
        total_days: number;
        days_with_complete_data: number;
        days_with_partial_data: number;
        days_with_no_data: number;
        average_data_quality_ratio: number;
        vital_categories: Record<string, string[]>;
      };
    };
  }

interface AbnormalVitalsAnalysisProps {
  data: ZScoresResponse;
  dailyKeyToData: Record<string, Record<string, number>>;
  isPrintMode?: boolean;
}

interface ProcessedVitalsData {
  abnormalVitals: Map<string, Array<{date: string; value: number; zscore: number}>>;
  abnormalVitalGroups: Map<string, string[]>;
  vitalsByPrefix: Map<string, Map<string, string[]>>;
}
  
const getVitalName = (zscoreField: string): string => {
  return zscoreField.split('_zscore')[0];
};

const getPrefix = (measurement: string): string => {
  const daytimeMatch = measurement.match(/(.*?)_(?:DAYTIME|NIGHTTIME)/);
  if (daytimeMatch) return daytimeMatch[1];
  
  const avgMatch = measurement.match(/(.*?)_(?:AVG|MIN|MAX)/);
  if (avgMatch) return avgMatch[1];

  return measurement;
};

interface VitalMapping {
  friendlyName: string;
  explanation: string;
}

const AbnormalVitalsAnalysis: React.FC<AbnormalVitalsAnalysisProps> = ({
  data,
  dailyKeyToData,
  isPrintMode = false
}) => {
  const [processedData, setProcessedData] = useState<ProcessedVitalsData | null>(null);

  interface VitalDataPoint {
    date: string;
    value: number | null;
    zscore: number;
    isAbnormal: boolean;
  }
  
  const getFriendlyName = (vitalName: string): string => {
    // Handle both cases with and without _zscore suffix
    const baseVitalName = vitalName.replace('_zscore', '');
    return (vitalsMap as Record<string, VitalMapping>)[baseVitalName]?.friendlyName || vitalName.split('_').join(' ');
  };

  const getVitalExplanation = (vitalName: string): string => {
    const baseVitalName = vitalName.replace('_zscore', '');
    return (vitalsMap as Record<string, VitalMapping>)[baseVitalName]?.explanation || '';
  };

  const getVitalData = (
    vitalName: string,
    zScoresData: ZScoreData[],
    dailyData: Record<string, Record<string, number>>
  ): VitalDataPoint[] => {
    const zscoreField = Object.keys(zScoresData[0] || {})
      .find(field => field.startsWith(vitalName) && field.endsWith('_zscore'));
    
    const scoresByDate = Object.fromEntries(
      zScoresData.map(score => [score.date.split('T')[0], score])
    );
    
    return Object.entries(dailyData[vitalName] || {})
      .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
      .map(([date, value]) => {
        const scoreData = scoresByDate[date.split('T')[0]];
        const zscore = scoreData?.[zscoreField as keyof ZScoreData] as number || 0;

        return {
          date,
          value: value || null,
          zscore,
          isAbnormal: Math.abs(zscore) > STD_THRESHOLD
        };
      });
  };

  useEffect(() => {
    const calculateAbnormalVitals = () => {
      const abnormalVitals = new Map<string, Array<{date: string; value: number; zscore: number}>>();
      const abnormalVitalGroups = new Map<string, string[]>();
      const vitalsByPrefix = new Map<string, Map<string, string[]>>();
      
      Object.entries(VITAL_TYPES).forEach(([vitalName, vitalType]) => {
        const zscoreFields = Object.keys(data.z_scores.scores[0] || {})
          .filter(field => field.startsWith(vitalName) && field.endsWith('_zscore'));
        
        zscoreFields.forEach(zscoreField => {
          const currentVitalName = getVitalName(zscoreField);
          const prefix = getPrefix(currentVitalName);
          const abnormalDays = data.z_scores.scores
            .filter(score => {
              const zscore = score[zscoreField as keyof ZScoreData];
              return typeof zscore === 'number' && Math.abs(zscore) > STD_THRESHOLD;
            })
            .map(score => ({
              date: score.date,
              zscore: score[zscoreField as keyof ZScoreData] as number,
              value: dailyKeyToData[currentVitalName]?.[score.date] || -1
            }));
  
          if (abnormalDays.length > 0) {
            abnormalVitals.set(currentVitalName, abnormalDays);
            
            const vitalGroup = vitalType.group;
            if (!abnormalVitalGroups.has(vitalGroup)) {
              abnormalVitalGroups.set(vitalGroup, []);
              vitalsByPrefix.set(vitalGroup, new Map());
            }
            
            const groupPrefixes = vitalsByPrefix.get(vitalGroup);
            if (groupPrefixes && !groupPrefixes.has(prefix)) {
              groupPrefixes.set(prefix, []);
            }
            
            groupPrefixes?.get(prefix)?.push(currentVitalName);
            if (!abnormalVitalGroups.get(vitalGroup)?.includes(prefix)) {
              abnormalVitalGroups.get(vitalGroup)?.push(prefix);
            }
          }
        });
      });
  
      setProcessedData({ abnormalVitals, abnormalVitalGroups, vitalsByPrefix });
    };
  
    calculateAbnormalVitals();
  }, [data, dailyKeyToData]);
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const dataPoint = context.raw;
            const baseVitalName = context.dataset.label;
            const unit = VITAL_TYPES[baseVitalName]?.unit || '';
            const value = dataPoint.y;
            const formattedValue = Math.abs(value) < 0.1 ? value?.toFixed(3) : value?.toFixed(1);
            return [
              `${getFriendlyName(baseVitalName)}`,
              `Value: ${formattedValue} ${unit}`,
              `Z-score: ${dataPoint.zscore?.toFixed(2)}`
            ];
          }
        }
      }
    },
    elements: {
      line: {
        tension: 0.4
      },
      point: {
        radius: (ctx: any) => ctx.raw?.isAbnormal ? 6 : 3,
        backgroundColor: (ctx: any) => ctx.raw?.isAbnormal ? '#ef4444' : ctx.dataset.borderColor,
        borderColor: (ctx: any) => ctx.raw?.isAbnormal ? '#ef4444' : ctx.dataset.borderColor,
        borderWidth: (ctx: any) => ctx.raw?.isAbnormal ? 2 : 1
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: { size: isPrintMode ? 8 : 13 }
        }
      },
      y: {
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { font: { size: isPrintMode ? 8 : 13 } }
      }
    }
  };

  if (!processedData) {
    return (
      <Card className={`${isPrintMode ? 'shadow-none border-0' : ''}`}>
        <Card.Body className={isPrintMode ? 'p-1' : 'p-3'}>
          <div className="text-center text-muted py-4">
            No extreme values detected
          </div>
        </Card.Body>
      </Card>
    );
  }

  const renderVitalsGroup = (
    group: string,
    prefixes: string[],
    processedData: ProcessedVitalsData,
    data: ZScoresResponse,
    dailyKeyToData: Record<string, Record<string, number>>,
    isPrintMode: boolean
  ) => {
    return (
      <Row className={`g-${isPrintMode ? '1' : '3'}`}>
        {prefixes.map((prefix: string) => {
          const vitalsInPrefix = processedData.vitalsByPrefix.get(group)?.get(prefix) || [];
          const vitalDatasets = vitalsInPrefix.map((vitalName: string) => ({
            vitalName,
            data: getVitalData(vitalName, data.z_scores.scores, dailyKeyToData)
          }));
          const vitalInfo = VITAL_TYPES[getPrefix(vitalsInPrefix[0])];
  
          const totalAbnormalDays = vitalsInPrefix.reduce((sum: number, vitalName: string) => 
            sum + (processedData.abnormalVitals.get(vitalName)?.length || 0), 0
          );
          
          return (
            <Col key={prefix} md={12}>
              <Card className="individual-plot mb-3">
                <div className={isPrintMode ? 'p-2' : 'p-3'}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className={`${isPrintMode ? 'h6' : ''} mb-0`}>
                      <FontAwesomeIcon 
                        icon={vitalInfo?.icon || faTriangleExclamation} 
                        className="me-2" 
                        style={{ color: vitalInfo?.color }}
                      />
                      {getFriendlyName(prefix)}
                    </h5>
                    <span className="badge bg-danger">
                      {totalAbnormalDays} extreme {totalAbnormalDays === 1 ? 'value' : 'values'}
                    </span>
                  </div>
                  
                  <div className="chart-container" style={{ 
                    minHeight: isPrintMode ? '180px' : '300px',
                    width: '100%'
                  }}>
                    <Line
                      data={{
                        labels: vitalDatasets
                          .flatMap(({ data }) => 
                            data.map((d: VitalDataPoint) => new Date(d.date).toLocaleDateString())
                          )
                          .filter((date: string, index: number, self: string[]) => self.indexOf(date) === index)
                          .sort(),
                        datasets: vitalDatasets.map(({ vitalName, data }, idx: number) => ({
                          label: getFriendlyName(vitalName),
                          data: data.map((d: VitalDataPoint) => ({
                            x: new Date(d.date),
                            y: d.value,
                            isAbnormal: d.isAbnormal,
                            zscore: d.zscore
                          })),
                          borderColor: `hsl(${(idx * 360) / vitalsInPrefix.length}, 70%, 50%)`,
                          backgroundColor: `hsla(${(idx * 360) / vitalsInPrefix.length}, 70%, 50%, 0.1)`,
                          borderWidth: 1,
                          fill: true
                        }))
                      }}
                      options={chartOptions}
                    />
                  </div>
                  <div className={`mt-3 ${isPrintMode ? 'text-sm' : ''}`}>
                    {vitalsInPrefix.map((vitalName: string) => (
                      <div key={vitalName} className="mb-2">
                        <p className="text-muted mb-1">
                          <strong>{getFriendlyName(vitalName)}:</strong>{' '}
                          {getVitalExplanation(vitalName)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };
  
  return (
    <Card className={`${isPrintMode ? 'shadow-none border-0' : ''}`}>
      {Array.from(processedData.abnormalVitalGroups.entries()).map(([group, prefixes], groupIndex) => {
        if (groupIndex === 0) {
          // First group - combine header with first plot
          return (
            <Card key={group} className="group-plots mb-4" style={{ pageBreakInside: 'avoid', breakInside: 'avoid', display: 'block' }}>
              <div className={`border-top ${isPrintMode ? 'py-2 px-1' : 'p-3'}`}>
                <h2 className={`${isPrintMode ? 'h5' : 'h3'} mb-0`}>
                  Most extreme values
                </h2>
                <div className="text-muted mb-3">
                  <small>Showing vitals with notable deviations ({'>'} {STD_THRESHOLD} standard deviations from average)</small>
                </div>
              </div>
              <div className={`bg-light ${isPrintMode ? 'py-2 px-1' : 'p-3'}`}>
                <h4 className={`${isPrintMode ? 'h6' : 'h5'} mb-0 text-capitalize`}>
                  {group} Metrics
                </h4>
              </div>
              <div className={isPrintMode ? 'p-2' : 'p-3'}>
                {renderVitalsGroup(group, prefixes, processedData, data, dailyKeyToData, isPrintMode)}
              </div>
            </Card>
          );
        }
  
        // Remaining groups
        return (
          <Card key={group} className="group-plots mb-4">
            <div className={`bg-light ${isPrintMode ? 'py-2 px-1' : 'p-3'}`}>
              <h4 className={`${isPrintMode ? 'h6' : 'h5'} mb-0 text-capitalize`}>
                {group} Metrics
              </h4>
            </div>
            <div className={isPrintMode ? 'p-2' : 'p-3'}>
              {renderVitalsGroup(group, prefixes, processedData, data, dailyKeyToData, isPrintMode)}
            </div>
          </Card>
        );
      })}
    </Card>
  );
};

export default AbnormalVitalsAnalysis;